import { computed, onScopeDispose, ref, shallowRef, type Ref } from 'vue'

import { workflowApi } from '../api/WorkflowApi'
import { SaveInstructionFlow } from '../flows/SaveInstructionFlow'
import type {
  WorkflowInstruction,
  WorkflowLoadStatus,
  WorkflowScope,
} from '../models/workflow.types'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'

export interface UseWorkflowInstructions {
  instructions: Ref<WorkflowInstruction[]>
  current: Readonly<Ref<WorkflowInstruction | null>>
  status: Ref<WorkflowLoadStatus>
  error: Ref<ApiError | null>
  fetch: () => Promise<void>
  save: (text: string) => Promise<boolean>
}

export function useWorkflowInstructions(): UseWorkflowInstructions {
  const { accountId } = useAccount()
  const { workspaceId } = useWorkspace()
  const toast = useToast()
  const { t } = useI18n()
  const instructions = ref<WorkflowInstruction[]>([])
  const status = ref<WorkflowLoadStatus>('idle')
  const error = shallowRef<ApiError | null>(null)
  let requestVersion = 0
  let controller: AbortController | null = null
  const scope = computed<WorkflowScope | null>(() =>
    accountId.value === undefined || workspaceId.value === undefined
      ? null
      : { accountId: accountId.value, workspaceId: workspaceId.value },
  )

  async function fetch(): Promise<void> {
    const currentScope = scope.value
    const version = ++requestVersion
    controller?.abort()
    if (!currentScope) {
      instructions.value = []
      status.value = 'idle'
      return
    }
    status.value = 'loading'
    error.value = null
    const activeController = new AbortController()
    controller = activeController
    try {
      const rows = await workflowApi.listInstructions(currentScope, activeController.signal)
      if (version !== requestVersion || activeController.signal.aborted) return
      instructions.value = rows
      status.value = 'ready'
    } catch (cause) {
      if (version !== requestVersion || activeController.signal.aborted) return
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      status.value = 'error'
    } finally {
      if (controller === activeController) controller = null
    }
  }

  onScopeDispose(() => {
    requestVersion += 1
    controller?.abort()
    controller = null
  })

  const saveFlow = new SaveInstructionFlow({
    api: workflowApi,
    notifySuccess: (updated) =>
      toast.success(t(updated ? 'workflow.instructions.updated' : 'workflow.instructions.created')),
    reload: fetch,
  })

  async function save(text: string): Promise<boolean> {
    if (!scope.value || !text.trim()) return false
    status.value = 'loading'
    error.value = null
    try {
      await saveFlow.execute(scope.value, text, instructions.value[0])
      return true
    } catch (cause) {
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      status.value = 'error'
      toast.error(t(error.value.i18nKey))
      return false
    }
  }

  return {
    instructions,
    current: computed(() => instructions.value[0] ?? null),
    status,
    error,
    fetch,
    save,
  }
}
